// src/components/CuratedFikaEntrepreneursAdmin.jsx
// Curated Fika Entrepreneurs Admin Dashboard - Complete Implementation
// Management interface for 1-on-1 founder/entrepreneur matching program

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import curatedFikaAdminService from '../services/curatedFikaAdminService';
import {
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  CogIcon,
  EnvelopeIcon,
  CalendarIcon,
  EyeIcon,
  PencilIcon,
  SparklesIcon,
  StarIcon,
  BoltIcon,
  HeartIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  PhotoIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  BriefcaseIcon,
  RocketLaunchIcon,
  LightBulbIcon,
  UserIcon
} from '@heroicons/react/24/outline';



// Quick Venue Add Form Component (reused from main admin)
const QuickVenueAddForm = ({ city, onVenueAdded, onCancel }) => {
  const [venueData, setVenueData] = useState({
    name: '',
    venue_type: 'cafe',
    address: '',
    venue_area: 'Shibuya & Harajuku',
    capacity_small: 4,
    description: '',
    phone: '',
    average_cost_per_person: 15.00,
    noise_level: 'moderate'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!venueData.name || !venueData.address) {
      alert('Please fill in venue name and address');
      return;
    }

    setLoading(true);
    try {
      const result = await curatedFikaAdminService.addVenue({
        ...venueData,
        city,
        country: 'Japan'
      });

      if (result.success) {
        onVenueAdded(result.data);
        alert('Venue added successfully!');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert(`Error adding venue: ${error.message}`);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">Add New Venue</h4>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircleIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Venue Name *
          </label>
          <input
            type="text"
            value={venueData.name}
            onChange={(e) => setVenueData({...venueData, name: e.target.value})}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="e.g., Blue Bottle Coffee Shibuya"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Venue Type
          </label>
          <select
            value={venueData.venue_type}
            onChange={(e) => setVenueData({...venueData, venue_type: e.target.value})}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="cafe">Cafe</option>
            <option value="restaurant">Restaurant</option>
            <option value="bar">Bar</option>
            <option value="hotel_lobby">Hotel Lobby</option>
            <option value="coworking">Coworking Space</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address *
        </label>
        <input
          type="text"
          value={venueData.address}
          onChange={(e) => setVenueData({...venueData, address: e.target.value})}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
          placeholder="Full address including building name"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Area
          </label>
          <select
            value={venueData.venue_area}
            onChange={(e) => setVenueData({...venueData, venue_area: e.target.value})}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="Shibuya & Harajuku">Shibuya & Harajuku</option>
            <option value="Ginza & Marunouchi">Ginza & Marunouchi</option>
            <option value="Omotesando & Aoyama">Omotesando & Aoyama</option>
            <option value="Shinjuku">Shinjuku</option>
            <option value="Roppongi">Roppongi</option>
            <option value="Nakameguro">Nakameguro</option>
            <option value="Setagaya City">Setagaya City</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Capacity (2-4 people)
          </label>
          <input
            type="number"
            value={venueData.capacity_small}
            onChange={(e) => setVenueData({...venueData, capacity_small: parseInt(e.target.value)})}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
            min="2"
            max="10"
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
        >
          {loading ? 'Adding...' : 'Add Venue'}
        </button>
      </div>
    </form>
  );
};

// Venue Selector Component (reused from main admin)
const VenueSelector = ({ selectedVenue, onVenueSelect, city = 'Tokyo' }) => {
  const [venues, setVenues] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    venue_type: '',
    venue_area: ''
  });
  
  useEffect(() => {
    loadVenues();
  }, [city, filters]);

  const loadVenues = async () => {
    setLoading(true);
    try {
      const result = await curatedFikaAdminService.getVenues({ 
        city, 
        ...filters 
      });
      if (result.success) {
        setVenues(result.data);
      }
    } catch (error) {
      console.error('Error loading venues:', error);
    }
    setLoading(false);
  };

  const handleVenueSelect = (venue) => {
    onVenueSelect({
      venue_name: venue.name,
      venue_address: venue.address,
      venue_area: venue.venue_area,
      venue_id: venue.id,
      manual_entry: false
    });
  };

  const handleVenueAdded = (newVenue) => {
    loadVenues();
    setShowAddForm(false);
    handleVenueSelect(newVenue);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Select Venue ({city})
        </label>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="text-sm text-purple-600 hover:text-purple-800 flex items-center"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Add New Venue
        </button>
      </div>

      {/* Venue Filters */}
      <div className="grid grid-cols-2 gap-3">
        <select
          value={filters.venue_type}
          onChange={(e) => setFilters({...filters, venue_type: e.target.value})}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
        >
          <option value="">All Types</option>
          <option value="cafe">Cafes</option>
          <option value="restaurant">Restaurants</option>
          <option value="bar">Bars</option>
          <option value="hotel_lobby">Hotel Lobbies</option>
        </select>
        
        <select
          value={filters.venue_area}
          onChange={(e) => setFilters({...filters, venue_area: e.target.value})}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
        >
          <option value="">All Areas</option>
          <option value="Shibuya & Harajuku">Shibuya & Harajuku</option>
          <option value="Ginza & Marunouchi">Ginza & Marunouchi</option>
          <option value="Omotesando & Aoyama">Omotesando & Aoyama</option>
          <option value="Shinjuku">Shinjuku</option>
          <option value="Roppongi">Roppongi</option>
          <option value="Setagaya City">Setagaya City</option>
          <option value="Nakameguro">Nakameguro</option>
        </select>
      </div>

      {/* Add Venue Form */}
      {showAddForm && (
        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
          <QuickVenueAddForm 
            city={city}
            onVenueAdded={handleVenueAdded}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {/* Venue Selection Grid */}
      {loading ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">Loading venues...</p>
        </div>
      ) : venues.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <BuildingOfficeIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-sm">No venues found for {city}</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="text-purple-600 hover:text-purple-800 text-sm mt-2"
          >
            Add the first venue
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
          {venues.map(venue => (
            <div
              key={venue.id}
              onClick={() => handleVenueSelect(venue)}
              className={`border rounded-lg p-4 cursor-pointer transition-all hover:border-purple-500 ${
                selectedVenue?.venue_id === venue.id 
                  ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="font-medium text-gray-900">{venue.name}</h4>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                      {venue.venue_type}
                    </span>
                    {venue.rating && (
                      <span className="text-yellow-500 text-sm">
                        ⭐ {venue.rating}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{venue.address}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>📍 {venue.venue_area}</span>
                    {venue.capacity_small && <span>👥 {venue.capacity_small} seats</span>}
                    {venue.noise_level && <span>🔊 {venue.noise_level}</span>}
                    {venue.usage_count > 0 && <span>Used {venue.usage_count}x</span>}
                  </div>
                </div>
                {venue.image_url ? (
                  <img 
                    src={venue.image_url} 
                    alt={venue.name}
                    className="w-16 h-16 rounded-lg object-cover ml-4"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center ml-4">
                    <PhotoIcon className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Fallback to Manual Entry */}
      <div className="border-t pt-4">
        <button
          onClick={() => onVenueSelect({ 
            venue_name: '', 
            venue_address: '', 
            venue_area: 'Shibuya & Harajuku',
            manual_entry: true 
          })}
          className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
        >
          <PencilIcon className="w-4 h-4 mr-1" />
          Enter venue details manually instead
        </button>
      </div>
    </div>
  );
};

const CuratedFikaEntrepreneursAdmin = () => {
  // State management
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [analytics, setAnalytics] = useState({
    topicsPopularity: [],
    stageDistribution: [],
    applicationAnalytics: []
  });

  const [selectedSession, setSelectedSession] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionDetailModal, setSessionDetailModal] = useState(false);
  const [showEditSessionModal, setShowEditSessionModal] = useState(false);

  // ADD THIS DEBUG EFFECT:
useEffect(() => {
  console.log('🔍 Modal state changed:', {
    showEditSessionModal,
    hasEditingSession: !!editingSession,
    editingSessionData: editingSession
  });
}, [showEditSessionModal, editingSession]);

  // Participant management
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [availableApplications, setAvailableApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState('');

  // Filters and pagination
  const [filters, setFilters] = useState({
    status: '',
    founder_role: '',
    company_stage: '',
    email: '',
    city: '',
    location_preference: ''
  });
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });

  const [matchingFilters, setMatchingFilters] = useState({
    city: 'Tokyo',
    country: 'Japan'
  });

  // Feedback state
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadApplications(),
        loadSessions(),
        loadAnalytics()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      alert('Error loading admin data');
    }
    setLoading(false);
  };

  // =====================================================
  // DATA LOADING FUNCTIONS
  // =====================================================

  const loadStats = async () => {
    try {
      // Use entrepreneur-specific analytics
      const analyticsResult = await curatedFikaAdminService.getEntrepreneurAnalytics();
      
      if (analyticsResult.success && analyticsResult.data) {
        // Calculate aggregate stats from analytics data
        const aggregateStats = {
          applications: {
            total: analyticsResult.data.reduce((sum, item) => sum + item.total_applications, 0),
            pending: analyticsResult.data
              .filter(item => item.application_status === 'submitted')
              .reduce((sum, item) => sum + item.total_applications, 0),
            approved: analyticsResult.data
              .filter(item => item.application_status === 'approved')
              .reduce((sum, item) => sum + item.total_applications, 0)
          },
          sessions: {
            total: 0, // Will be updated from sessions data
            active: 0
          },
          revenue: {
            thisMonth: 0 // Will be calculated from sessions
          },
          trends: {
            applicationsThisWeek: 0
          }
        };
        
        setStats(aggregateStats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

   const updateEntrepreneurSessionStatus = async (sessionId, newStatus) => {
    setLoading(true);
    try {
      const result = await curatedFikaAdminService.updateEntrepreneurSessionStatus(
        sessionId,
        newStatus
      );
      
      if (result.success) {
        // Different success messages for different transitions
        const messages = {
          'confirmed': '✅ Session marked as confirmed!',
          'completed': '✅ Session marked as completed! Participants updated to "attended" status.',
          'cancelled': '✅ Session cancelled!',
          'in_progress': '✅ Session marked as in progress!',
          'scheduled': '✅ Session status updated to scheduled!'
        };
        
        alert(messages[newStatus] || '✅ Session status updated!');
        loadSessions();
        loadStats();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert(`❌ Error updating status: ${error.message}`);
      loadSessions(); // Reload to reset dropdown to correct value
    }
    setLoading(false);
  };

  const loadApplications = async () => {
    try {
      const result = await curatedFikaAdminService.getAllEntrepreneurApplications(filters);
      
      if (result.success && result.data) {
        setApplications(Array.isArray(result.data) ? result.data : []);
        setPagination(prev => ({ ...prev, total: result.data.length || 0 }));
      } else {
        setApplications([]);
      }
    } catch (error) {
      console.error('Error loading entrepreneur applications:', error);
      setApplications([]);
    }
  };

  const loadSessions = async () => {
    try {
      // Get entrepreneur-specific sessions
      const result = await curatedFikaAdminService.getEntrepreneurSessions({
        ...filters,
        session_type: 'entrepreneur_1on1'
      });
      
      if (result.success && result.data) {
        setSessions(Array.isArray(result.data) ? result.data : []);
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error('Error loading entrepreneur sessions:', error);
      setSessions([]);
    }
  };

  const loadAnalytics = async () => {
    try {
      const [topicsResult, stagesResult, analyticsResult] = await Promise.all([
        curatedFikaAdminService.getEntrepreneurTopicsPopularity(),
        curatedFikaAdminService.getCompanyStageDistribution(),
        curatedFikaAdminService.getEntrepreneurAnalytics()
      ]);

      setAnalytics({
        topicsPopularity: topicsResult.success ? topicsResult.data : [],
        stageDistribution: stagesResult.success ? stagesResult.data : [],
        applicationAnalytics: analyticsResult.success ? analyticsResult.data : []
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  // Load available applications for adding participants
  const loadAvailableApplications = async () => {
    try {
      const result = await curatedFikaAdminService.getAllEntrepreneurApplications({
        status: 'approved'
      });
      
      if (result.success) {
        // Filter out applications already in this session
        const currentParticipantIds = selectedSession.curated_fika_entrepreneur_participants?.map(p => p.application_id) || [];
        const available = result.data.filter(app => 
          !currentParticipantIds.includes(app.id)
        );
        setAvailableApplications(available);
      }
    } catch (error) {
      console.error('Error loading available applications:', error);
    }
  };

  // =====================================================
  // ENTREPRENEUR-SPECIFIC HELPER FUNCTIONS
  // =====================================================



/**
 * Determines if a participant has confirmed attendance
 * ROBUST: Checks both status field AND timestamp existence
 */
const isParticipantConfirmed = (participant) => {
  if (!participant) return false;
  
  // PRIMARY CHECK: participation_status must be 'confirmed'
  const hasConfirmedStatus = participant.participation_status === 'confirmed';
  
  // SECONDARY CHECK: confirmed_at must be a valid timestamp (not null, not empty string)
  const hasConfirmedTimestamp = 
    participant.confirmed_at !== null && 
    participant.confirmed_at !== undefined &&
    participant.confirmed_at !== '' &&
    participant.confirmed_at !== 'null'; // Guard against string "null"
  
  // BOTH must be true for genuine confirmation
  return hasConfirmedStatus && hasConfirmedTimestamp;
};

 /**
 * Gets the display status for a participant
 * Returns human-readable status based on actual confirmation state
 */
const getParticipantDisplayStatus = (participant) => {
  if (!participant) return 'unknown';
  
  // Use the helper function to determine true confirmation
  if (isParticipantConfirmed(participant)) {
    return 'confirmed';
  }
  
  // Handle special cases
  if (participant.participation_status === 'paid') {
    return 'paid'; // Paid but not confirmed
  }
  
  if (participant.participation_status === 'attended') {
    return 'attended';
  }
  
  if (participant.participation_status === 'cancelled') {
    return 'cancelled';
  }
  
  if (participant.participation_status === 'no_show') {
    return 'no_show';
  }
  
  // Default to actual status or 'invited' if unknown
  return participant.participation_status || 'invited';
};


  const getCompanyStageColor = (stage) => {
    const colors = {
      'Idea Stage': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Building MVP': 'bg-orange-100 text-orange-800 border-orange-200',
      'Pre-Revenue': 'bg-blue-100 text-blue-800 border-blue-200',
      'Early Revenue (<$100K ARR)': 'bg-green-100 text-green-800 border-green-200',
      'Growing ($100K-$1M ARR)': 'bg-teal-100 text-teal-800 border-teal-200',
      'Scaling ($1M+ ARR)': 'bg-purple-100 text-purple-800 border-purple-200',
      'Solopreneur/Freelancer': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'Side Project': 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getFounderRoleIcon = (role) => {
    const icons = {
      'Solo Founder': '🏃',
      'Co-Founder': '👥',
      'Solopreneur': '💼',
      'Freelancer': '🎨',
      'Early-Stage Operator': '🚀',
      'Growth-Stage Operator': '📈',
      'Serial Entrepreneur': '🎯',
      'First-Time Founder': '🌱',
      'Product Manager': '⚙️',
      'Technical Lead': '💻'
    };
    return icons[role] || '🏗️';
  };

  const getTopicIcon = (topic) => {
    const icons = {
      'Product Development': '🛠️',
      'Growth & Marketing': '📈',
      'Fundraising': '💰',
      'Hiring & Team Building': '👥',
      'Sales & Business Development': '🤝',
      'Operations & Systems': '⚙️',
      'AI & Technology': '🤖',
      'Sustainability': '🌱',
      'International Expansion': '🌍',
      'Work-Life Balance': '⚖️',
      'Pricing Strategy': '💵',
      'Customer Discovery': '🔍'
    };
    return icons[topic] || '💡';
  };

  // =====================================================
  // ACTION HANDLERS
  // =====================================================

  const updateApplicationStatus = async (applicationId, newStatus, notes = '') => {
    if (!confirm(`Update application status to "${newStatus}"?`)) return;

    setLoading(true);
    try {
      const result = await curatedFikaAdminService.updateEntrepreneurApplicationStatus(
        applicationId,
        newStatus,
        notes
      );

      if (result.success) {
        alert('Application status updated successfully!');
        loadApplications();
        loadStats();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

const handleCreateManual1on1Session = () => {
  console.log('🔍 Create 1-on-1 clicked!', {
    selectedItemsCount: selectedItems.length,
    selectedItems: selectedItems
  });

  if (selectedItems.length !== 2) {
    alert('Please select exactly 2 applications to create a 1-on-1 session.');
    return;
  }

  const app1 = applications.find(app => app.id === selectedItems[0]);
  const app2 = applications.find(app => app.id === selectedItems[1]);

  if (!app1 || !app2) {
    alert('Error: Could not find selected applications');
    return;
  }

  const sessionData = {
    session_title: `Founders 1-on-1: ${app1.first_name} & ${app2.first_name}`,
    session_date_local: '',
    venue_name: '',
    venue_address: '',
    venue_area: 'Shibuya & Harajuku',
    city: app1.city || 'Tokyo',
    country: app1.country || 'Japan',
    max_participants: 2,
    session_duration: 90,
    price_per_person: 30.00,
    host_notes: '',
    manual_entry: true,
    isNew: true,
    applicationIds: selectedItems
  };

  // Set both states together using React.startTransition or batch them
  setEditingSession(sessionData);
  setTimeout(() => {
    setShowEditSessionModal(true);
  }, 0);
};


  const handleSaveNewSession = async () => {
    if (!editingSession.isNew) {
      return handleEditEntrepreneurSession();
    }

    if (!editingSession.session_date_local || !editingSession.venue_name) {
      alert('Please fill in session date and venue name');
      return;
    }

    setLoading(true);
    try {
      const sessionData = {
        session_title: editingSession.session_title,
        session_date: editingSession.session_date_local,
        venue_name: editingSession.venue_name,
        venue_address: editingSession.venue_address,
        venue_area: editingSession.venue_area,
        venue_id: editingSession.venue_id,
        city: editingSession.city,
        country: editingSession.country,
        session_duration: editingSession.session_duration,
        price_per_person: editingSession.price_per_person,
        host_notes: editingSession.host_notes,
        session_type: 'entrepreneur_1on1',
        max_participants: 2
      };

      const result = await curatedFikaAdminService.createEntrepreneurSessionFromApplications(
        editingSession.applicationIds,
        sessionData
      );

      if (result.success) {
        alert(`✅ 1-on-1 session created successfully!\n\nSession: "${editingSession.session_title}"\nParticipants: 2 founders`);
        setShowEditSessionModal(false);
        setEditingSession(null);
        setSelectedItems([]);
        loadSessions();
        loadStats();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const handleEditEntrepreneurSession = async () => {
    if (!editingSession) return;

    if (!confirm('Update 1-on-1 session details?')) return;

    setLoading(true);
    try {
      const updatesToSend = { ...editingSession };
      
      // Handle datetime conversion
      if (editingSession.session_date_local) {
        updatesToSend.session_date = editingSession.session_date_local;
        delete updatesToSend.session_date_local;
      }

      // Remove UI-only fields
      delete updatesToSend.isNew;
      delete updatesToSend.applicationIds;
      delete updatesToSend.manual_entry;
      delete updatesToSend.curated_fika_entrepreneur_participants;

      const result = await curatedFikaAdminService.updateEntrepreneurSessionDetails(
        editingSession.id,
        updatesToSend
      );

      if (result.success) {
        alert('1-on-1 session updated successfully!');
        setShowEditSessionModal(false);
        setEditingSession(null);
        loadSessions();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const runAIMatching = async () => {
    if (!matchingFilters.city || !matchingFilters.country) {
      alert('Please select both city and country before running AI matching.');
      return;
    }

    if (!confirm(`Run AI matching for 1-on-1 entrepreneur sessions in ${matchingFilters.city}, ${matchingFilters.country}?\n\nThis will create optimized founder pairs based on stage, topics, and challenges.`)) return;

    setLoading(true);
    try {
      // Call entrepreneur-specific matching endpoint
      const result = await curatedFikaAdminService.runAIMatchingV2({
        city: matchingFilters.city,
        country: matchingFilters.country,
        session_type: 'entrepreneur_1on1',
        max_participants: 2 // Force 1-on-1 sessions
      });

      if (result.success && result.sessionsCreated > 0) {
        alert(`AI matching completed for ${matchingFilters.city}, ${matchingFilters.country}!\n\n${result.sessionsCreated} new 1-on-1 sessions created\nProcessed ${result.applicationsProcessed} applications`);
        loadApplications();
        loadSessions();
        loadStats();
      } else {
        alert(result.message || `No sessions were created for ${matchingFilters.city}, ${matchingFilters.country}. Need more compatible applications.`);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const handleDeleteSession = async (session) => {
    const participantCount = session.curated_fika_entrepreneur_participants?.length || 0;
    const confirmedCount = session.curated_fika_entrepreneur_participants?.filter(p => p.participation_status === 'confirmed').length || 0;
    const paidCount = session.curated_fika_entrepreneur_participants?.filter(p => p.payment_status === 'paid').length || 0;

    let warningMessage = `⚠️ DELETE SESSION CONFIRMATION\n\n`;
    warningMessage += `Session: "${session.session_title}"\n`;
    warningMessage += `Date: ${formatDate(session.session_date)}\n`;
    warningMessage += `Status: ${session.session_status}\n`;
    warningMessage += `Participants: ${participantCount}\n`;
    
    if (confirmedCount > 0) {
      warningMessage += `⚠️ Confirmed Participants: ${confirmedCount}\n`;
    }
    
    if (paidCount > 0) {
      warningMessage += `💰 Paid Participants: ${paidCount}\n`;
      warningMessage += `\n❌ Cannot delete session with paid participants.`;
      alert(warningMessage);
      return;
    }
    
    warningMessage += `\n🗑️ This action CANNOT be undone.\n`;
    warningMessage += `Are you sure you want to delete this session?`;

    if (!confirm(warningMessage)) return;

    setLoading(true);
    try {
      const result = await curatedFikaAdminService.deleteEntrepreneurSession(session.id, {
        acknowledged_confirmations: confirmedCount > 0
      });

      if (result.success) {
        alert(`✅ Session deleted successfully!\n\nParticipants affected: ${result.participants_affected || 0}`);
        loadSessions();
        loadStats();
      } else {
        alert(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
    setLoading(false);
  };

  const handleDeleteParticipant = async (participant, session) => {
    const member = participant.curated_fika_entrepreneurs;
    const participantName = `${member.first_name} ${member.last_name}`;
    
    let confirmMessage = `⚠️ REMOVE PARTICIPANT\n\n`;
    confirmMessage += `Participant: ${participantName}\n`;
    confirmMessage += `Email: ${member.email}\n`;
    confirmMessage += `Session: "${session.session_title}"\n`;
    confirmMessage += `Status: ${participant.participation_status}\n`;
    confirmMessage += `Payment: ${participant.payment_status}\n\n`;
    
    if (participant.payment_status === 'paid') {
      confirmMessage += `💰 This participant has already paid.\n`;
      confirmMessage += `Consider processing a refund before removal.\n\n`;
    }
    
    confirmMessage += `This action CANNOT be undone.\n\n`;
    confirmMessage += `Are you sure you want to remove ${participantName}?`;

    if (!confirm(confirmMessage)) return;

    setLoading(true);
    try {
      const result = await curatedFikaAdminService.deleteEntrepreneurParticipant(participant.id);

      if (result.success) {
        alert(`✅ Participant removed successfully!`);
        
        // Refresh session details
        if (selectedSession) {
          const updatedSession = await curatedFikaAdminService.getEntrepreneurSessionDetails(selectedSession.id);
          if (updatedSession.success) {
            setSelectedSession(updatedSession.data);
          }
        }
        
        loadSessions();
        loadStats();
      } else {
        alert(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
    setLoading(false);
  };

  const handleAddParticipant = async () => {
    if (!selectedApplicationId) {
      alert('Please select an application');
      return;
    }

    if (!confirm(`Add participant to "${selectedSession.session_title}"?`)) return;

    setLoading(true);
    try {
      const result = await curatedFikaAdminService.addParticipantToEntrepreneurSession(
        selectedSession.id,
        selectedApplicationId,
        {
          send_invitation_email: true
        }
      );

      if (result.success) {
        alert(`✅ Participant added successfully!`);
        
        // Refresh session details
        const updatedSession = await curatedFikaAdminService.getEntrepreneurSessionDetails(selectedSession.id);
        if (updatedSession.success) {
          setSelectedSession(updatedSession.data);
        }
        
        setShowAddParticipant(false);
        setSelectedApplicationId('');
        loadSessions();
        loadStats();
      } else {
        alert(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
    setLoading(false);
  };

  // Filter handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const applyFilters = () => {
    loadApplications();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      founder_role: '',
      company_stage: '',
      email: '',
      city: '',
      location_preference: ''
    });
    setPagination(prev => ({ ...prev, offset: 0 }));
    setTimeout(loadApplications, 100);
  };

  // Utility functions
  const getStatusColor = (status) => {
    return curatedFikaAdminService.getStatusColor(status);
  };

  const formatDate = (dateString) => {
    return curatedFikaAdminService.formatDate(dateString);
  };

  const formatDateTime = (dateString) => {
    return curatedFikaAdminService.formatDateTime(dateString);
  };

  const convertUTCToLocal = (utcDateString, timezone) => {
    if (!utcDateString) return '';
    
    const utcDate = new Date(utcDateString);
    const localTime = utcDate.toLocaleString('sv-SE', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return localTime.replace(' ', 'T');
  };

  // Tab navigation
  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'applications', name: 'Applications', icon: BriefcaseIcon },
    { id: 'sessions', name: '1-on-1 Sessions', icon: UserGroupIcon },
    { id: 'matching', name: 'AI Matching', icon: BoltIcon },
    { id: 'analytics', name: 'Analytics', icon: StarIcon }
  ];

  if (loading && !stats && !applications.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading entrepreneur admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <RocketLaunchIcon className="w-8 h-8 text-orange-500 mr-3" />
                Founders 1-on-1 Admin
              </h1>
              <p className="text-gray-600">Manage entrepreneur matching program</p>
            </div>
            <div className="flex items-center space-x-3">
              {loading && (
                <div className="flex items-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                  Loading...
                </div>
              )}
              <button
                onClick={loadInitialData}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center text-sm"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex space-x-8">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <BriefcaseIcon className="w-8 h-8 text-orange-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Applications</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.applications?.total || 0}</p>
                      <p className="text-xs text-gray-500">
                        Founder applications
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <ClockIcon className="w-8 h-8 text-yellow-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Pending Review</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.applications?.pending || 0}</p>
                      <p className="text-xs text-gray-500">
                        Awaiting approval
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <UserGroupIcon className="w-8 h-8 text-purple-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">1-on-1 Sessions</p>
                      <p className="text-2xl font-bold text-gray-900">{sessions.length || 0}</p>
                      <p className="text-xs text-gray-500">
                        Founder matches
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <ChartBarIcon className="w-8 h-8 text-green-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Success Rate</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {stats.applications?.approved > 0 
                          ? Math.round((sessions.length / stats.applications.approved) * 100) 
                          : 0}%
                      </p>
                      <p className="text-xs text-gray-500">
                        Approval to match
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={runAIMatching}
                  className="bg-orange-600 text-white p-4 rounded-lg hover:bg-orange-700 flex items-center justify-center"
                >
                  <BoltIcon className="w-5 h-5 mr-2" />
                  Run 1-on-1 AI Matching
                </button>
                
                <button
                  onClick={() => setActiveTab('applications')}
                  className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                >
                  <EyeIcon className="w-5 h-5 mr-2" />
                  Review Applications
                </button>

                <button
                  onClick={() => setActiveTab('sessions')}
                  className="bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 flex items-center justify-center"
                >
                  <UserGroupIcon className="w-5 h-5 mr-2" />
                  Manage 1-on-1s
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Entrepreneur Applications</h3>
              <div className="space-y-3">
                {applications.slice(0, 5).map(app => (
                  <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-lg">{getFounderRoleIcon(app.founder_role)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {app.first_name} {app.last_name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {app.founder_role} • {app.company_stage}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(app.application_status)}`}>
                        {app.application_status}
                      </span>
                      <span className="text-sm text-gray-500">{formatDate(app.submitted_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Entrepreneur Applications</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="submitted">Submitted</option>
                    <option value="under_review">Under Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="waitlist">Waitlist</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Founder Role</label>
                  <select
                    value={filters.founder_role}
                    onChange={(e) => handleFilterChange('founder_role', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Roles</option>
                    <option value="Solo Founder">Solo Founder</option>
                    <option value="Co-Founder">Co-Founder</option>
                    <option value="Solopreneur">Solopreneur</option>
                    <option value="Freelancer">Freelancer</option>
                    <option value="Early-Stage Operator">Early-Stage Operator</option>
                    <option value="Growth-Stage Operator">Growth-Stage Operator</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Stage</label>
                  <select
                    value={filters.company_stage}
                    onChange={(e) => handleFilterChange('company_stage', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Stages</option>
                    <option value="Idea Stage">Idea Stage</option>
                    <option value="Building MVP">Building MVP</option>
                    <option value="Pre-Revenue">Pre-Revenue</option>
                    <option value="Early Revenue (<$100K ARR)">Early Revenue</option>
                    <option value="Growing ($100K-$1M ARR)">Growing</option>
                    <option value="Scaling ($1M+ ARR)">Scaling</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <select
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Cities</option>
                    <option value="Tokyo">Tokyo</option>
                    <option value="Osaka">Osaka</option>
                  </select>
                </div>

                <div className="flex items-end space-x-2">
                  <button
                    onClick={applyFilters}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center"
                  >
                    <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                    Filter
                  </button>
                  <button
                    onClick={clearFilters}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedItems.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-800 font-medium">
                      {selectedItems.length} applications selected
                    </p>
                    {selectedItems.length === 2 && (
                      <p className="text-sm text-orange-600 mt-1">
                        ✓ Perfect! Ready to create a 1-on-1 session
                      </p>
                    )}
                    {selectedItems.length !== 2 && (
                      <p className="text-sm text-orange-600 mt-1">
                        Select exactly 2 applications to create a 1-on-1
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCreateManual1on1Session}
                      className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 flex items-center text-sm disabled:opacity-50"
                      disabled={selectedItems.length !== 2}
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      Create 1-on-1 ({selectedItems.length}/2)
                    </button>
                    <button
                      onClick={() => setSelectedItems([])}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 text-sm"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Applications List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Entrepreneur Applications ({pagination.total})
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems(applications.map(app => app.id));
                            } else {
                              setSelectedItems([]);
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Founder
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role & Stage
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Challenge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Topics & Goals
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Applied
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {applications.map(app => (
                      <tr key={app.id} className={selectedItems.includes(app.id) ? 'bg-orange-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(app.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems(prev => [...prev, app.id]);
                              } else {
                                setSelectedItems(prev => prev.filter(id => id !== app.id));
                              }
                            }}
                            className="rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {app.first_name} {app.last_name}
                            </div>
                            <div className="text-sm text-gray-500">{app.email}</div>
                            {app.company_name && (
                              <div className="text-xs text-gray-400">{app.company_name}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="flex items-center mb-1">
                              <span className="mr-2">{getFounderRoleIcon(app.founder_role)}</span>
                              <span className="font-medium text-gray-900">{app.founder_role}</span>
                            </div>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCompanyStageColor(app.company_stage)}`}>
                              {app.company_stage}
                            </span>
                            {app.industry && (
                              <div className="text-xs text-gray-500 mt-1">{app.industry}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs">
                            {app.current_challenge ? (
                              <p className="line-clamp-2">{app.current_challenge}</p>
                            ) : (
                              <span className="text-gray-400 italic">Not specified</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm max-w-xs">
                            {app.entrepreneur_topics && app.entrepreneur_topics.length > 0 && (
                              <div className="mb-2">
                                {app.entrepreneur_topics.slice(0, 2).map((topic, idx) => (
                                  <span key={idx} className="inline-block bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs mr-1 mb-1">
                                    {getTopicIcon(topic)} {topic}
                                  </span>
                                ))}
                                {app.entrepreneur_topics.length > 2 && (
                                  <span className="text-xs text-gray-500">+{app.entrepreneur_topics.length - 2} more</span>
                                )}
                              </div>
                            )}
                            {app.entrepreneur_goals && app.entrepreneur_goals.length > 0 && (
                              <div>
                                {app.entrepreneur_goals.slice(0, 2).map((goal, idx) => (
                                  <span key={idx} className="inline-block bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs mr-1 mb-1">
                                    {goal}
                                  </span>
                                ))}
                                {app.entrepreneur_goals.length > 2 && (
                                  <span className="text-xs text-gray-500">+{app.entrepreneur_goals.length - 2} more</span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.application_status)}`}>
                            {app.application_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(app.submitted_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-1">
                            {app.application_status === 'submitted' && (
                              <>
                                <button
                                  onClick={() => updateApplicationStatus(app.id, 'approved')}
                                  className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs hover:bg-green-200"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => updateApplicationStatus(app.id, 'rejected')}
                                  className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                const details = `Founder Application Details:\n\nName: ${app.first_name} ${app.last_name}\nEmail: ${app.email}\nRole: ${app.founder_role}\nStage: ${app.company_stage}\nIndustry: ${app.industry || 'Not specified'}\n${app.company_name ? `Company: ${app.company_name}\n` : ''}Current Challenge: ${app.current_challenge || 'Not specified'}\nLooking For: ${app.looking_for || 'Not specified'}\nTopics: ${app.entrepreneur_topics?.join(', ') || 'None'}\nGoals: ${app.entrepreneur_goals?.join(', ') || 'None'}\nCommunication Style: ${app.communication_style || 'Not specified'}\nLocation: ${app.location_preference || 'Flexible'}`;
                                alert(details);
                              }}
                              className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs hover:bg-blue-200"
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    1-on-1 Founder Sessions ({sessions.length})
                  </h3>
                  <div className="text-sm text-gray-600">
                    All sessions are 1-on-1 founder matches
                  </div>
                </div>
              </div>
              
              {sessions.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p>No 1-on-1 sessions found. Run AI matching to create sessions.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {sessions.map(session => (
                    <div key={session.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-lg font-medium text-gray-900">
                              {session.session_title}
                            </h4>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${curatedFikaAdminService.getStatusColor(session.session_status)}`}>
                              {session.session_status}
                            </span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              1-on-1
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                            <div>
                              <span className="font-medium">Date:</span> {formatDate(session.session_date)}
                            </div>
                            <div>
                              <span className="font-medium">Venue:</span> {session.venue_name}
                            </div>
                            <div>
                              <span className="font-medium">Area:</span> {session.venue_area}
                            </div>
                            <div>
                              <span className="font-medium">Participants:</span> {session.current_participants}/2
                            </div>
                          </div>

                          {/* Participants Preview */}
                           
                          <div className="flex items-center space-x-4">
                            

                            <div className="flex items-center space-x-2">
                                <UserGroupIcon className="w-4 h-4 text-gray-400" />
                                <div className="flex -space-x-1">
                                    {(session.curated_fika_entrepreneur_participants || []).map((participant, idx) => {
                                    const isConfirmed = isParticipantConfirmed(participant);
                                    return (
                                        <div
                                        key={idx}
                                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium border-2 border-white relative ${
                                            isConfirmed 
                                            ? 'bg-green-100 text-green-700' 
                                            : 'bg-orange-100 text-orange-600'
                                        }`}
                                        title={`${participant.curated_fika_entrepreneurs?.first_name} ${participant.curated_fika_entrepreneurs?.last_name} - ${isConfirmed ? 'Confirmed' : 'Invited'}`}
                                        >
                                        {participant.curated_fika_entrepreneurs?.first_name?.[0]}{participant.curated_fika_entrepreneurs?.last_name?.[0]}
                                        {isConfirmed && (
                                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border border-white"></span>
                                        )}
                                        </div>
                                    );
                                    })}
                                </div>
                            </div>
                            
                            <div className="text-sm">
                              <span className="font-medium">Revenue:</span> ${session.total_revenue || (session.price_per_person * session.current_participants).toFixed(2)}
                            </div>
                          </div>

                          
                        

                        </div>

                        <div className="flex items-center space-x-2">
                            {/* Send Invitations Button */}
                            {session.session_status === 'scheduled' && session.current_participants > 0 && (
                                <button
                                onClick={async () => {
                                    if (!confirm(`Send invitation emails to all ${session.current_participants} participants?`)) return;
                                    
                                    setLoading(true);
                                    try {
                                    const result = await curatedFikaAdminService.sendEntrepreneurSessionInvitations(session.id);
                                    if (result.success) {
                                        alert(`✅ Invitations sent to ${session.current_participants} participants!`);
                                        loadSessions();
                                    } else {
                                        alert(`❌ Error: ${result.error}`);
                                    }
                                    } catch (error) {
                                    alert(`❌ Error: ${error.message}`);
                                    }
                                    setLoading(false);
                                }}
                                className="bg-purple-100 text-purple-700 px-3 py-2 rounded-md hover:bg-purple-200 flex items-center text-sm"
                                disabled={loading}
                                >
                                <EnvelopeIcon className="w-4 h-4 mr-1" />
                                Send Invites
                                </button>
                            )}

                            {/* Confirm Session Button */}
                            {session.session_status === 'scheduled' && (
                                <button
                                onClick={async () => {
                                    if (!confirm('Mark this session as confirmed?')) return;
                                    
                                    setLoading(true);
                                    try {
                                    const result = await curatedFikaAdminService.updateEntrepreneurSessionStatus(
                                        session.id,
                                        'confirmed'
                                    );
                                    if (result.success) {
                                        alert('✅ Session marked as confirmed!');
                                        loadSessions();
                                    } else {
                                        alert(`❌ Error: ${result.error}`);
                                    }
                                    } catch (error) {
                                    alert(`❌ Error: ${error.message}`);
                                    }
                                    setLoading(false);
                                }}
                                className="bg-green-100 text-green-700 px-3 py-2 rounded-md hover:bg-green-200 flex items-center text-sm"
                                disabled={loading}
                                >
                                <CheckCircleIcon className="w-4 h-4 mr-1" />
                                Confirm
                                </button>
                            )}

                            {/* Complete Session Button */}
                            {session.session_status === 'confirmed' && (
                                <button
                                onClick={async () => {
                                    if (!confirm('Mark this session as completed?')) return;
                                    
                                    setLoading(true);
                                    try {
                                    const result = await curatedFikaAdminService.updateEntrepreneurSessionStatus(
                                        session.id,
                                        'completed'
                                    );
                                    if (result.success) {
                                        alert('✅ Session marked as completed!');
                                        loadSessions();
                                    } else {
                                        alert(`❌ Error: ${result.error}`);
                                    }
                                    } catch (error) {
                                    alert(`❌ Error: ${error.message}`);
                                    }
                                    setLoading(false);
                                }}
                                className="bg-blue-100 text-blue-700 px-3 py-2 rounded-md hover:bg-blue-200 flex items-center text-sm"
                                disabled={loading}
                                >
                                <CheckCircleIcon className="w-4 h-4 mr-1" />
                                Complete
                                </button>
                            )}

                            {/* Cancel Button */}
                            {(session.session_status === 'scheduled' || session.session_status === 'confirmed') && (
                                <button
                                onClick={async () => {
                                    if (!confirm('Cancel this session? Participants will be notified.')) return;
                                    
                                    setLoading(true);
                                    try {
                                    const result = await curatedFikaAdminService.updateEntrepreneurSessionStatus(
                                        session.id,
                                        'cancelled'
                                    );
                                    if (result.success) {
                                        alert('✅ Session cancelled!');
                                        loadSessions();
                                    } else {
                                        alert(`❌ Error: ${result.error}`);
                                    }
                                    } catch (error) {
                                    alert(`❌ Error: ${error.message}`);
                                    }
                                    setLoading(false);
                                }}
                                className="bg-yellow-100 text-yellow-700 px-3 py-2 rounded-md hover:bg-yellow-200 flex items-center text-sm"
                                disabled={loading}
                                >
                                <XCircleIcon className="w-4 h-4 mr-1" />
                                Cancel
                                </button>
                            )}

                            {/* Details Button */}
                            <button
                                onClick={async () => {
                                setLoading(true);
                                try {
                                    const result = await curatedFikaAdminService.getEntrepreneurSessionDetails(session.id);
                                    if (result.success) {
                                    setSelectedSession(result.data);
                                    setSessionDetailModal(true);
                                    } else {
                                    alert('Error loading session details: ' + result.error);
                                    }
                                } catch (error) {
                                    alert('Error: ' + error.message);
                                }
                                setLoading(false);
                                }}
                                className="bg-blue-100 text-blue-700 px-3 py-2 rounded-md hover:bg-blue-200 flex items-center text-sm"
                                disabled={loading}
                            >
                                <EyeIcon className="w-4 h-4 mr-1" />
                                Details
                            </button>

                            {/* Edit Button */}
                            <button
                                onClick={() => {
                                const timezone = session.venue_timezone || 'Asia/Tokyo';
                                const localDateTimeString = session.session_date 
                                    ? convertUTCToLocal(session.session_date, timezone)
                                    : '';
                                
                                setEditingSession({
                                    ...session,
                                    session_date_local: localDateTimeString,
                                    manual_entry: true,
                                    isNew: false
                                });
                                setShowEditSessionModal(true);
                                }}
                                className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 flex items-center text-sm"
                                disabled={loading}
                            >
                                <PencilIcon className="w-4 h-4 mr-1" />
                                Edit
                            </button>

                            {/* Delete Button */}
                            <button
                                onClick={() => handleDeleteSession(session)}
                                className="bg-red-100 text-red-700 px-3 py-2 rounded-md hover:bg-red-200 flex items-center text-sm"
                                disabled={loading}
                            >
                                <TrashIcon className="w-4 h-4 mr-1" />
                                Delete
                            </button>

                            {/* ===================================================== */}
                            {/* STATUS DROPDOWN - Provides full status control      */}
                            {/* Matches functionality from CuratedFikaAdmin.jsx     */}
                            {/* ===================================================== */}
                            <div className="relative">
                                <select
                                    value={session.session_status}
                                    onChange={(e) => {
                                    const newStatus = e.target.value;
                                    const currentStatus = session.session_status;
                                    
                                    // Prevent no-op changes
                                    if (newStatus === currentStatus) return;
                                    
                                    // Smart confirmation messages based on transition
                                    let confirmMessage = `Change session status from "${currentStatus}" to "${newStatus}"?`;
                                    
                                    // Special warnings for critical transitions
                                    if (newStatus === 'completed') {
                                        confirmMessage = `Mark session as completed?\n\n` +
                                        `This will automatically update all confirmed participants to "completed" status.`;
                                    } else if (newStatus === 'cancelled') {
                                        confirmMessage = `Cancel this session?\n\n` +
                                        `This action should be used for sessions that won't happen. ` +
                                        `Participants will need to be notified separately.`;
                                    } else if (newStatus === 'confirmed' && currentStatus === 'completed') {
                                        confirmMessage = `⚠️ REVERTING COMPLETED SESSION\n\n` +
                                        `Move session back from "completed" to "confirmed"?\n\n` +
                                        `Note: Participant statuses will NOT be automatically reverted. ` +
                                        `You'll need to manually update participant statuses if needed.`;
                                    } else if (newStatus === 'scheduled' && 
                                                (currentStatus === 'confirmed' || currentStatus === 'completed')) {
                                        confirmMessage = `⚠️ REVERTING SESSION STATUS\n\n` +
                                        `Move session back to "scheduled"?\n\n` +
                                        `This is unusual and may confuse participants who already confirmed.`;
                                    }
                                    
                                    if (!confirm(confirmMessage)) {
                                        // Reset dropdown to current value if user cancels
                                        e.target.value = currentStatus;
                                        return;
                                    }
                                    
                                    // Proceed with update
                                    updateEntrepreneurSessionStatus(session.id, newStatus);
                                    }}
                                    className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-orange-500 focus:border-orange-500 bg-white"
                                    disabled={loading}
                                    title="Change session status"
                                >
                                    <option value="scheduled">Scheduled</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>


                        </div>

                      


                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>



          </div>
        )}

        {/* Matching Tab */}
        {activeTab === 'matching' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BoltIcon className="w-6 h-6 text-orange-600 mr-3" />
                AI Matching for 1-on-1 Founder Sessions
              </h3>

              <div className="bg-blue-50 rounded-lg p-4mb-6">
                <h4 className="font-medium text-blue-900 mb-3">Geographic Scope</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">
                      City *
                    </label>
                    <select
                      value={matchingFilters.city || 'Tokyo'}
                      onChange={(e) => setMatchingFilters(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full border border-blue-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="Tokyo">Tokyo</option>
                      <option value="Osaka">Osaka</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">
                      Country *
                    </label>
                    <select
                      value={matchingFilters.country || 'Japan'}
                      onChange={(e) => setMatchingFilters(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full border border-blue-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="Japan">Japan</option>
                    </select>
                  </div>
                </div>
                <p className="text-sm text-blue-700 mt-2">
                  Matching will only include entrepreneur applications from the selected city and country
                </p>
              </div>
              
              <div className="bg-orange-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-orange-900 mb-2">How 1-on-1 AI Matching Works:</h4>
                <ul className="text-sm text-orange-800 space-y-1">
                  <li>• Matches founders at similar or complementary company stages</li>
                  <li>• Pairs tactical needs with relevant expertise (your challenge = their experience)</li>
                  <li>• Considers communication style and work-life balance preferences</li>
                  <li>• Optimizes for location and schedule compatibility</li>
                  <li>• Creates exactly 2-person sessions for deep, focused conversations</li>
                  <li>• Prioritizes approved applications with high compatibility</li>
                </ul>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={runAIMatching}
                  disabled={loading}
                  className="bg-orange-600 text-white px-8 py-3 rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center text-lg font-medium"
                >
                  <BoltIcon className="w-6 h-6 mr-3" />
                  {loading ? 'Running AI Matching...' : `Run 1-on-1 Matching for ${matchingFilters.city || 'Tokyo'}, ${matchingFilters.country || 'Japan'}`}
                </button>
              </div>

              {stats && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{stats.applications?.approved || 0}</div>
                    <div className="text-sm text-gray-600">Approved Applications</div>
                    <div className="text-xs text-gray-500 mt-1">Ready for matching</div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{sessions.length || 0}</div>
                    <div className="text-sm text-gray-600">1-on-1 Sessions</div>
                    <div className="text-xs text-gray-500 mt-1">Successfully created</div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {stats.applications?.approved >= 2 ? Math.floor((stats.applications?.approved || 0) / 2) : 0}
                    </div>
                    <div className="text-sm text-gray-600">Potential Matches</div>
                    <div className="text-xs text-gray-500 mt-1">From approved apps</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Topics Popularity */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Most Popular Entrepreneur Topics
              </h3>
              <div className="space-y-3">
                {analytics.topicsPopularity.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {getTopicIcon(item.topic)} {item.topic}
                        </span>
                        <span className="text-sm text-gray-600">
                          {item.topic_count} applications ({item.percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Company Stage Distribution */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Company Stage Distribution
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analytics.stageDistribution.map((item, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border-2 ${getCompanyStageColor(item.company_stage)}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">{item.company_stage}</h4>
                        <p className="text-sm opacity-80">
                          {item.stage_count} founders ({item.percentage}%)
                        </p>
                      </div>
                      <div className="text-3xl font-bold opacity-80">
                        {item.stage_count}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Application Trends */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Application Trends Over Time
              </h3>
              <div className="text-center py-8 text-gray-500">
                <ChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p>Advanced analytics visualization coming soon</p>
                <p className="text-sm mt-2">Track trends, conversion rates, and matching success</p>
              </div>
            </div>
          </div>
        )}

          {/* Session Detail Modal */}
            {sessionDetailModal && selectedSession && (
               <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">1-on-1 Session Details</h3>
                    <button
                      onClick={() => setSessionDetailModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircleIcon className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Session Information */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Session Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Title:</span> {selectedSession.session_title}
                        </div>
                        <div>
                          <span className="font-medium">Status:</span> 
                          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs ${curatedFikaAdminService.getStatusColor(selectedSession.session_status)}`}>
                            {selectedSession.session_status}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Date:</span> {formatDateTime(selectedSession.session_date)}
                        </div>
                        <div>
                          <span className="font-medium">Duration:</span> {selectedSession.session_duration || 90} minutes
                        </div>
                        <div>
                          <span className="font-medium">Venue:</span> {selectedSession.venue_name}
                        </div>
                        <div>
                          <span className="font-medium">Address:</span> {selectedSession.venue_address}
                        </div>
                        <div>
                          <span className="font-medium">Area:</span> {selectedSession.venue_area}
                        </div>
                        <div>
                          <span className="font-medium">Price per Person:</span> ${selectedSession.price_per_person}
                        </div>
                      </div>
                      
                      {selectedSession.host_notes && (
                        <div className="mt-3">
                          <span className="font-medium">Host Notes:</span>
                          <p className="text-gray-600 mt-1">{selectedSession.host_notes}</p>
                        </div>
                      )}
                    </div>

                    {/* Participants */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Founders in this 1-on-1</h4>
                      <div className="space-y-3">
                        {(selectedSession.curated_fika_entrepreneur_participants || []).map((participant, idx) => {
                          const member = participant.curated_fika_entrepreneurs;
                          if (!member) return null;
                          
                          return (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-xl">{getFounderRoleIcon(member.founder_role)}</span>
                                    <h5 className="font-medium text-gray-900">
                                      {member.first_name} {member.last_name}
                                    </h5>
                                  </div>
                                  <p className="text-sm text-gray-600">{member.email}</p>
                                </div>

                                <div className="flex space-x-2">
                                {/* Confirmation Status Badge */}
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    isParticipantConfirmed(participant)
                                    ? 'bg-green-100 text-green-800 border border-green-300' 
                                    : curatedFikaAdminService.getStatusColor(participant.participation_status)
                                }`}>
                                    {isParticipantConfirmed(participant) ? (
                                    <>
                                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                                        Confirmed
                                    </>
                                    ) : (
                                    getParticipantDisplayStatus(participant)
                                    )}
                                </span>

                                {/* Show confirmation timestamp when confirmed */}
                                {isParticipantConfirmed(participant) && participant.confirmed_at && (
                                    <span className="text-xs text-gray-500 flex items-center">
                                    <ClockIcon className="w-3 h-3 mr-1" />
                                    {formatDateTime(participant.confirmed_at)}
                                    </span>
                                )}

                                {/* Payment Status Badge */}
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    participant.payment_status === 'paid' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {participant.payment_status}
                                </span>

                                {/* Delete Participant Button (only for invited/confirmed states) */}
                                {(participant.participation_status === 'invited' || 
                                    participant.participation_status === 'confirmed') && (
                                    <button
                                    onClick={() => handleDeleteParticipant(participant, selectedSession)}
                                    className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200 flex items-center"
                                    disabled={loading}
                                    >
                                    <TrashIcon className="w-3 h-3 mr-1" />
                                    Remove
                                    </button>
                                )}
                                </div>


                              </div>
                              
                              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                                <div>
                                  <span className="font-medium">Role:</span> {member.founder_role}
                                </div>
                                <div>
                                  <span className="font-medium">Stage:</span> 
                                  <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs ${getCompanyStageColor(member.company_stage)}`}>
                                    {member.company_stage}
                                  </span>
                                </div>
                                {member.industry && (
                                  <div>
                                    <span className="font-medium">Industry:</span> {member.industry}
                                  </div>
                                )}
                                {member.company_name && (
                                  <div>
                                    <span className="font-medium">Company:</span> {member.company_name}
                                  </div>
                                )}
                              </div>

                              {member.current_challenge && (
                                <div className="mb-3">
                                  <span className="font-medium text-sm text-gray-700">Current Challenge:</span>
                                  <p className="text-sm text-gray-600 mt-1">{member.current_challenge}</p>
                                </div>
                              )}

                              {member.looking_for && (
                                <div className="mb-3">
                                  <span className="font-medium text-sm text-gray-700">Looking For:</span>
                                  <p className="text-sm text-gray-600 mt-1">{member.looking_for}</p>
                                </div>
                              )}

                              {member.entrepreneur_topics && member.entrepreneur_topics.length > 0 && (
                                <div className="mb-3">
                                  <span className="font-medium text-sm text-gray-700">Topics:</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {member.entrepreneur_topics.map((topic, i) => (
                                      <span key={i} className="inline-block bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                                        {getTopicIcon(topic)} {topic}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {member.entrepreneur_goals && member.entrepreneur_goals.length > 0 && (
                                <div>
                                  <span className="font-medium text-sm text-gray-700">Goals:</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {member.entrepreneur_goals.map((goal, i) => (
                                      <span key={i} className="inline-block bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">
                                        {goal}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Add Participant Section */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {!showAddParticipant ? (
                          <button
                            onClick={() => {
                              setShowAddParticipant(true);
                              loadAvailableApplications();
                            }}
                            className="bg-green-100 text-green-700 px-3 py-2 rounded-md hover:bg-green-200 flex items-center text-sm"
                            disabled={selectedSession.current_participants >= 2}
                          >
                            <PlusIcon className="w-4 h-4 mr-1" />
                            Add Participant ({selectedSession.current_participants}/2)
                          </button>
                        ) : (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h5 className="font-medium text-green-800 mb-3">Add New Participant</h5>
                            
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Select Application
                                </label>
                                <select
                                  value={selectedApplicationId}
                                  onChange={(e) => setSelectedApplicationId(e.target.value)}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-green-500 focus:border-green-500"
                                >
                                  <option value="">-- Select an approved application --</option>
                                  {availableApplications.map(app => (
                                    <option key={app.id} value={app.id}>
                                      {app.first_name} {app.last_name} ({app.email}) - {app.founder_role}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              <div className="flex space-x-2">
                                <button
                                  onClick={handleAddParticipant}
                                  disabled={!selectedApplicationId || loading}
                                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                                >
                                  Add & Send Invitation
                                </button>
                                <button
                                  onClick={() => {
                                    setShowAddParticipant(false);
                                    setSelectedApplicationId('');
                                  }}
                                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                            
                            {availableApplications.length === 0 && (
                              <p className="text-sm text-gray-600 mt-2">
                                No available approved applications to add.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

        
            {/* Edit Entrepreneur Session Modal */}
            {showEditSessionModal && editingSession && (
               <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingSession.isNew ? 'Create 1-on-1 Session' : 'Edit 1-on-1 Session'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowEditSessionModal(false);
                        setEditingSession(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircleIcon className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-orange-800">
                        <strong>1-on-1 Format:</strong> This session will connect exactly 2 founders for 
                        intimate, focused conversation about their challenges and experiences.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Session Title *
                        </label>
                        <input
                          type="text"
                          value={editingSession.session_title}
                          onChange={(e) => setEditingSession({
                            ...editingSession,
                            session_title: e.target.value
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="e.g., Founders 1-on-1: Product & Growth"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Session Date & Time *
                          <span className="text-sm font-medium text-blue-600 ml-2">
                            ({editingSession?.city || 'Tokyo'} local time)
                          </span>
                        </label>
                        <input
                          type="datetime-local"
                          value={editingSession.session_date_local || ''}
                          onChange={(e) => setEditingSession({
                            ...editingSession,
                            session_date_local: e.target.value
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>

                    {/* Venue Selection */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-700">Venue Selection</h4>
                        <button
                          onClick={() => setEditingSession({
                            ...editingSession,
                            manual_entry: !editingSession.manual_entry
                          })}
                          className="text-sm text-orange-600 hover:text-orange-800"
                        >
                          {editingSession.manual_entry ? 'Use Venue Selector' : 'Manual Entry'}
                        </button>
                      </div>

                      {editingSession.manual_entry ? (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Venue Name *
                              </label>
                              <input
                                type="text"
                                value={editingSession.venue_name}
                                onChange={(e) => setEditingSession({
                                  ...editingSession,
                                  venue_name: e.target.value
                                })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                                placeholder="e.g., Onibus Coffee"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Venue Area
                              </label>
                              <select
                                value={editingSession.venue_area}
                                onChange={(e) => setEditingSession({
                                  ...editingSession,
                                  venue_area: e.target.value
                                })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                              >
                                <option value="Shibuya & Harajuku">Shibuya & Harajuku</option>
                                <option value="Ginza & Marunouchi">Ginza & Marunouchi</option>
                                <option value="Omotesando & Aoyama">Omotesando & Aoyama</option>
                                <option value="Shinjuku">Shinjuku</option>
                                <option value="Roppongi">Roppongi</option>
                                <option value="Nakameguro">Nakameguro</option>
                                <option value="Setagaya City">Setagaya City</option>
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Venue Address
                            </label>
                            <input
                              type="text"
                              value={editingSession.venue_address}
                              onChange={(e) => setEditingSession({
                                ...editingSession,
                                venue_address: e.target.value
                              })}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                              placeholder="Full address"
                            />
                          </div>
                        </>
                      ) : (
                        <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                          <VenueSelector
                            selectedVenue={editingSession}
                            city={editingSession.city || 'Tokyo'}
                            onVenueSelect={(venueData) => setEditingSession({
                              ...editingSession,
                              ...venueData
                            })}
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (minutes)
                        </label>
                        <input
                          type="number"
                          value={editingSession.session_duration || 90}
                          onChange={(e) => setEditingSession({
                            ...editingSession,
                            session_duration: parseInt(e.target.value)
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price per Person ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={editingSession.price_per_person}
                          onChange={(e) => setEditingSession({
                            ...editingSession,
                            price_per_person: parseFloat(e.target.value)
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Host Notes
                      </label>
                      <textarea
                        value={editingSession.host_notes || ''}
                        onChange={(e) => setEditingSession({
                          ...editingSession,
                          host_notes: e.target.value
                        })}
                        rows={3}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Any special instructions or focus areas for this 1-on-1..."
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <button
                        onClick={() => {
                          setShowEditSessionModal(false);
                          setEditingSession(null);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveNewSession}
                        disabled={loading}
                        className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                      >
                        {loading ? 'Saving...' : (editingSession.isNew ? 'Create Session' : 'Save Changes')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}


      </div>


    </div>
  );
};

export default CuratedFikaEntrepreneursAdmin;